import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, MessageCircle, Instagram } from "lucide-react";
import { COMPANY, LOGO_URL, SERVICES } from "@/data";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.6 6.7a5.1 5.1 0 0 1-3-1 5.1 5.1 0 0 1-2-4h-3.4v13.6a2.6 2.6 0 1 1-1.9-2.5V9.3a6 6 0 1 0 5.3 6V9.7a8.5 8.5 0 0 0 5 1.6V7.9a5 5 0 0 1 0-1.2Z"/>
  </svg>
);

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative bg-ink text-white grain overflow-hidden">
      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8 py-20">
        <div className="grid gap-12 md:grid-cols-4">
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <img src={LOGO_URL} alt="M&A Objektservice" className="h-12 w-12 rounded-full object-cover ring-1 ring-gold/40" />
              <span className="font-serif text-xl">M&amp;A <span className="text-gold">Objektservice</span></span>
            </div>
            <p className="text-white/50 text-sm leading-relaxed max-w-xs">
              {COMPANY.slogan}. Alles rund um Ihr Objekt – aus einer Hand.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href={COMPANY.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="social-instagram"
                aria-label="Instagram"
                className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white/70 hover:text-gold hover:border-gold/40 transition-colors"
              >
                <Instagram size={18} />
              </a>
              <a
                href={COMPANY.tiktok}
                target="_blank"
                rel="noreferrer"
                data-testid="social-tiktok"
                aria-label="TikTok"
                className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white/70 hover:text-gold hover:border-gold/40 transition-colors"
              >
                <TikTokIcon size={16} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-gold text-xs uppercase tracking-[0.2em] mb-5 font-sans font-medium">Leistungen</h4>
            <ul className="space-y-2.5 text-sm text-white/60">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to="/leistungen" className="hover:text-gold transition-colors">{s.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-gold text-xs uppercase tracking-[0.2em] mb-5 font-sans font-medium">Navigation</h4>
            <ul className="space-y-2.5 text-sm text-white/60">
              <li><Link to="/" className="hover:text-gold transition-colors">Start</Link></li>
              <li><Link to="/ueber-uns" className="hover:text-gold transition-colors">Über uns</Link></li>
              <li><Link to="/kontakt" className="hover:text-gold transition-colors">Kontakt</Link></li>
              <li><Link to="/impressum" className="hover:text-gold transition-colors">Impressum</Link></li>
              <li><Link to="/datenschutz" className="hover:text-gold transition-colors">Datenschutz</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-gold text-xs uppercase tracking-[0.2em] mb-5 font-sans font-medium">Kontakt</h4>
            <ul className="space-y-3.5 text-sm text-white/60">
              <li className="flex gap-3"><MapPin size={16} className="text-gold shrink-0 mt-0.5" /><span>{COMPANY.address}</span></li>
              <li><a href={`tel:${COMPANY.phoneRaw}`} className="flex gap-3 hover:text-gold transition-colors"><Phone size={16} className="text-gold shrink-0 mt-0.5" />{COMPANY.phone}</a></li>
              <li><a href={`mailto:${COMPANY.email}`} className="flex gap-3 hover:text-gold transition-colors"><Mail size={16} className="text-gold shrink-0 mt-0.5" />{COMPANY.email}</a></li>
              <li><a href={COMPANY.whatsapp} target="_blank" rel="noreferrer" className="flex gap-3 hover:text-gold transition-colors"><MessageCircle size={16} className="text-gold shrink-0 mt-0.5" />WhatsApp</a></li>
              <li className="flex gap-3"><Clock size={16} className="text-gold shrink-0 mt-0.5" /><span>{COMPANY.hours}</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/40">
          <span>© {new Date().getFullYear()} M&amp;A Objektservice GbR · Inhaber: {COMPANY.owners}</span>
          <span>Einsatzgebiet: {COMPANY.area}</span>
        </div>
      </div>
    </footer>
  );
}
